import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Server, MapPin, Check, Phone } from 'lucide-react';
import { Eyebrow } from '../ui/Section';
import { Button } from '../ui/Button';
import { BrandCurve } from '../ui/BrandCurve';
import { IMAGES, CONTACT } from '../../lib/constants';
const trustSignals = [{
  icon: ShieldCheck,
  label: 'Professional-services specialist'
}, {
  icon: Server,
  label: 'CT Group-operated private infrastructure'
}, {
  icon: MapPin,
  label: 'National support across Australia'
}];
const nodes = [{
  label: 'Support',
  color: '#0AA77E',
  x: '16%',
  y: '18%'
}, {
  label: 'Security',
  color: '#D2223D',
  x: '84%',
  y: '24%'
}, {
  label: 'Hosting',
  color: '#57578E',
  x: '14%',
  y: '80%'
}, {
  label: 'Voice',
  color: '#F79429',
  x: '86%',
  y: '76%'
}];
/** Elegant environment diagram: central CT node + four service nodes on orbit lines. */
function EnvironmentDiagram() {
  return <div className="pointer-events-none absolute inset-0" aria-hidden data-id="element-208">
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 400 400" fill="none" data-id="element-209">
        <circle cx="200" cy="200" r="118" stroke="rgba(255,255,255,0.18)" strokeWidth="1" data-id="element-210" />
        <circle cx="200" cy="200" r="168" stroke="rgba(255,255,255,0.10)" strokeWidth="1" data-id="element-211" />
        <path d="M200 200 L74 82" stroke="rgba(255,255,255,0.20)" strokeWidth="1" data-id="element-212" />
        <path d="M200 200 L328 104" stroke="rgba(255,255,255,0.20)" strokeWidth="1" data-id="element-213" />
        <path d="M200 200 L66 316" stroke="rgba(255,255,255,0.20)" strokeWidth="1" data-id="element-214" />
        <path d="M200 200 L336 300" stroke="rgba(255,255,255,0.20)" strokeWidth="1" data-id="element-215" />
      </svg>

      <motion.div initial={{
      scale: 0.85,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} className="absolute left-1/2 top-1/2 flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-ct-cyan text-center shadow-cyan ring-4 ring-white/25" data-id="element-216">
        <span className="text-[11px] font-black uppercase tracking-wide text-white" data-id="element-217">
          CT Group
        </span>
        <span className="text-[9px] font-medium text-white/85" data-id="element-218">
          One partner
        </span>
      </motion.div>

      {nodes.map((n, i) => <motion.div key={n.label} initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} transition={{
      duration: 0.4,
      delay: 0.3 + i * 0.12,
      ease: 'easeOut'
    }} className="absolute flex h-[52px] w-[52px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-center shadow-lg ring-2 ring-white/30" style={{
      left: n.x,
      top: n.y,
      background: n.color
    }} data-id="element-219">
          <span className="px-1 text-[10px] font-bold leading-tight text-white" data-id="element-220">
            {n.label}
          </span>
        </motion.div>)}
    </div>;
}
export function Hero() {
  return <section id="top" className="relative w-full overflow-hidden bg-navy" aria-label="Introduction" data-id="element-221">
      {/* soft depth field on dark hero */}
      <div className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(120% 90% at 85% 0%, rgba(0,168,232,0.18), transparent 55%), radial-gradient(90% 80% at 0% 100%, rgba(0,78,128,0.35), transparent 60%)'
    }} aria-hidden data-id="element-222" />
      {/* CT Group signature brand swoosh — warmth + brand identity */}
      <BrandCurve className="-left-40 -top-32 h-[560px] w-[560px] opacity-90 md:-left-24" data-id="element-223" />
      <div className="relative mx-auto grid max-w-content grid-cols-1 items-center gap-10 px-5 py-16 md:px-8 md:py-20 lg:grid-cols-2 lg:gap-12 lg:py-24" data-id="element-224">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-225">
          <Eyebrow onDark color="var(--ct-light-blue)" data-id="element-226">
            Managed IT for professional services
          </Eyebrow>
          <h1 className="mt-6 font-display text-[40px] font-black leading-[1.04] tracking-tight text-white md:text-[52px] lg:text-[60px]" data-id="element-227">
            Managed IT for professional firms that{' '}
            <span className="text-ct-light" data-id="element-228">cannot afford disruption.</span>
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-white/75 md:text-[19px]" data-id="element-229">
            CT Group manages your full technology environment — support,
            security, hosting, voice, software, licensing and users — so your
            people can work with less disruption and your firm can move forward
            with confidence.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-230">
            <Button as="a" href="#it-review" variant="primary" size="lg" data-id="element-231">
              Book an independent IT Review
              <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" aria-hidden data-id="element-232" />
            </Button>
            <Button as="a" href="#how" variant="ghost" size="lg" data-id="element-233">
              See how CT Group works
            </Button>
          </div>
          <p className="mt-4 text-[14px] text-white/55" data-id="element-234">
            A practical, non-intrusive review. No forced change of provider.
          </p>

          <ul className="mt-10 grid grid-cols-1 gap-3 border-t border-white/10 pt-7 sm:grid-cols-3" data-id="element-235">
            {trustSignals.map(t => <li key={t.label} className="flex items-start gap-2.5 text-[13.5px] font-semibold text-white/90" data-id="element-236">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-ct-cyan/15" data-id="element-237">
                  <Check className="h-3.5 w-3.5 text-ct-light" aria-hidden data-id="element-238" />
                </span>
                {t.label}
              </li>)}
          </ul>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="relative" data-id="element-239">
          <div className="relative overflow-hidden rounded-panel ring-1 ring-white/15 shadow-lg" data-id="element-240">
            <img src={IMAGES.hero} alt="Partners and a practice manager reviewing information together in a modern Australian office at dusk" className="h-[440px] w-full object-cover md:h-[520px]" data-id="element-241" />
            <div className="absolute inset-0" style={{
            background: 'linear-gradient(160deg, rgba(6,24,43,0.72) 0%, rgba(6,24,43,0.28) 52%, rgba(6,24,43,0.12) 100%)'
          }} aria-hidden data-id="element-242" />
            <EnvironmentDiagram data-id="element-243" />
            <div className="absolute inset-x-0 bottom-0 p-5" data-id="element-244">
              <div className="rounded-2xl border border-white/15 bg-navy/55 px-4 py-3 backdrop-blur-md" data-id="element-245">
                <p className="text-[13px] font-semibold text-white" data-id="element-246">
                  Your whole technology environment — brought under one
                  accountable partner.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* seamless transition to first light section */}
      <div className="relative h-px w-full bg-white/5" aria-hidden data-id="element-247" />
    </section>;
}